import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { hasManagerAccess } from '../App';
import { TrendingUp, Trash2, Clock, CheckCircle, Users } from 'lucide-react';

const fmt = (n) => '$' + (n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputCls = 'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-nova-500 focus:border-nova-500 outline-none text-sm';

const STATUS_STYLE = {
  pending:          { label: 'Pending',       cls: 'bg-yellow-100 text-yellow-700' },
  color_match:      { label: 'Color Match',   cls: 'bg-purple-100 text-purple-700' },
  confirmed:        { label: 'Confirmed',     cls: 'bg-blue-100 text-blue-700' },
  scheduled:        { label: 'Scheduled',     cls: 'bg-indigo-100 text-indigo-700' },
  in_progress:      { label: 'Installing',    cls: 'bg-orange-100 text-orange-700' },
  awaiting_payment: { label: 'Awaiting Pay',  cls: 'bg-amber-100 text-amber-700' },
  completed:        { label: 'Completed',     cls: 'bg-green-100 text-green-700' },
  cancelled:        { label: 'Cancelled',     cls: 'bg-gray-100 text-gray-500' },
};

function StatCard({ label, value, color, subtitle, icon: Icon }) {
  return (
    <div className={`bg-white rounded-xl border p-5 flex flex-col gap-1`}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{fmt(value)}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  );
}

export default function Commissions() {
  const { user } = useAuth();
  const isAdmin = hasManagerAccess(user);

  const [salesmen, setSalesmen] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [payoutForm, setPayoutForm] = useState({
    amount: '',
    paid_date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [savingPayout, setSavingPayout] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      api.get('/commissions').then(setSalesmen);
    } else {
      // Salesman: auto-select self
      setSelectedId(user.id);
    }
  }, [isAdmin, user]);

  useEffect(() => {
    if (!selectedId) return;
    setLoadingDetail(true);
    api.get(`/commissions/${selectedId}`)
      .then(setDetail)
      .finally(() => setLoadingDetail(false));
  }, [selectedId]);

  const handleLogPayout = async (e) => {
    e.preventDefault();
    if (!payoutForm.amount || !selectedId) return;
    setSavingPayout(true);
    try {
      await api.post('/commissions/payout', {
        salesman_id: selectedId,
        amount: Number(payoutForm.amount),
        paid_date: payoutForm.paid_date,
        notes: payoutForm.notes,
      });
      setPayoutForm({ amount: '', paid_date: new Date().toISOString().split('T')[0], notes: '' });
      const updated = await api.get(`/commissions/${selectedId}`);
      setDetail(updated);
      if (isAdmin) {
        const updatedSalesmen = await api.get('/commissions');
        setSalesmen(updatedSalesmen);
      }
    } catch (e) {
      alert(e.message);
    } finally {
      setSavingPayout(false);
    }
  };

  const handleDeletePayout = async (payoutId) => {
    if (!confirm('Delete this payout record?')) return;
    await api.delete(`/commissions/payout/${payoutId}`);
    const updated = await api.get(`/commissions/${selectedId}`);
    setDetail(updated);
    if (isAdmin) {
      const updatedSalesmen = await api.get('/commissions');
      setSalesmen(updatedSalesmen);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="w-7 h-7 text-nova-600" />
        <h1 className="text-2xl font-bold">Commission Tracker</h1>
      </div>

      {/* Admin: salesman cards */}
      {isAdmin && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
          {salesmen.map(s => (
            <button
              key={s.id}
              onClick={() => setSelectedId(s.id)}
              className={`text-left bg-white rounded-xl border p-4 hover:shadow-md transition-shadow ${selectedId === s.id ? 'border-nova-500 ring-2 ring-nova-500' : ''}`}>
              <p className="font-semibold text-gray-800 text-sm">{s.full_name}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {(s.commission_rate * 100).toFixed(0)}% rate &middot; {s.sale_count} sales
                {s.manager_sale_count > 0 && ` · +${s.manager_sale_count} override`}
              </p>
              <div className="mt-2 space-y-1 border-t pt-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Owed now</span>
                  <span className={`font-semibold ${s.balance_owed > 0 ? 'text-orange-500' : 'text-gray-400'}`}>
                    {fmt(s.balance_owed)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>
                  <span className="font-medium text-blue-500">{fmt(s.pending_commission)}</span>
                </div>
              </div>
            </button>
          ))}
          {salesmen.length === 0 && (
            <p className="col-span-4 text-gray-400 text-sm">No salesmen found.</p>
          )}
        </div>
      )}

      {/* Alternative pay types (admin shortcuts) */}
      {isAdmin && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Inventory pay (Jiwan, Kushdeep) */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center justify-between">
            <div>
              <p className="font-semibold text-emerald-800 text-sm">Inventory Pay</p>
              <p className="text-xs text-emerald-600 mt-0.5">Paid per channel added (e.g. Jiwan, Kushdeep)</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {salesmen.filter(s => {
                const n = s.full_name?.toLowerCase() || '';
                return n.includes('jiwan') || n.includes('kushdeep');
              }).map(s => (
                <a key={s.id} href={`/inventory-pay/${s.id}`}
                  className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-3 py-1.5 rounded-lg whitespace-nowrap">
                  {s.full_name} →
                </a>
              ))}
            </div>
          </div>
          {/* Install pay (Miguel) */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex items-center justify-between">
            <div>
              <p className="font-semibold text-orange-800 text-sm">Install Pay</p>
              <p className="text-xs text-orange-600 mt-0.5">Paid per foot installed (e.g. Miguel)</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {salesmen.filter(s => s.full_name?.toLowerCase().includes('miguel')).map(s => (
                <a key={s.id} href={`/install-pay/${s.id}`}
                  className="text-xs bg-orange-600 hover:bg-orange-700 text-white font-medium px-3 py-1.5 rounded-lg whitespace-nowrap">
                  {s.full_name} →
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Detail panel */}
      {selectedId && (
        <>
          {loadingDetail && <p className="text-gray-400 text-sm">Loading...</p>}
          {detail && !loadingDetail && (
            <div className="space-y-6">
              {/* Stats row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  label="Earned (Installed)"
                  value={detail.earnedCommission}
                  color="text-green-600"
                  icon={CheckCircle}
                  subtitle="Installed + collected"
                />
                <StatCard
                  label="Pending (Pipeline)"
                  value={detail.pendingCommission}
                  color="text-blue-500"
                  icon={Clock}
                  subtitle="Not yet installed"
                />
                <StatCard
                  label="Paid Out"
                  value={detail.totalPaid}
                  color="text-gray-600"
                />
                <StatCard
                  label="Balance Owed Now"
                  value={detail.balanceOwed}
                  color={detail.balanceOwed > 0 ? 'text-orange-500' : 'text-gray-400'}
                  subtitle="Earned − Paid Out"
                />
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-2.5 text-xs text-blue-700 flex items-start gap-2">
                <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <p>
                  <span className="font-semibold">Pending</span> commissions become <span className="font-semibold">Earned</span> automatically
                  once a sale reaches <span className="font-semibold">Completed</span> status (installed + payment collected).
                  {detail.managerSales?.length > 0 && <> Includes your own sales plus any override earnings from team sales you manage.</>}
                </p>
              </div>

              {/* Sales breakdown */}
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <h2 className="font-semibold text-gray-700">
                    {detail.managerSales?.length > 0 ? 'Your Own Sales' : 'Sales Breakdown'} — {detail.salesman.full_name}
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Customer</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Deal Total</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Pre-Tax</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Rate</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Commission</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {detail.sales.map(s => {
                        const st = STATUS_STYLE[s.status] || { label: s.status, cls: 'bg-gray-100 text-gray-500' };
                        const isEarned = s.status === 'completed';
                        return (
                          <tr key={s.id} className={`hover:bg-gray-50 ${isEarned ? '' : 'bg-blue-50/30'}`}>
                            <td className="px-4 py-3 text-gray-500">{s.created_at?.split('T')[0]}</td>
                            <td className="px-4 py-3 font-medium">{s.customer_name}</td>
                            <td className="px-4 py-3">{fmt(s.deal_total)}</td>
                            <td className="px-4 py-3">{fmt(s.pre_tax_amount)}</td>
                            <td className="px-4 py-3">{((s.commission_rate || 0) * 100).toFixed(2)}%</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                            </td>
                            <td className={`px-4 py-3 font-semibold ${isEarned ? 'text-green-600' : 'text-blue-500'}`}>
                              {fmt(s.commission_amount)}
                              {!isEarned && <span className="ml-1 text-[10px] font-normal text-blue-400">pending</span>}
                            </td>
                          </tr>
                        );
                      })}
                      {detail.sales.length === 0 && (
                        <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">No sales</td></tr>
                      )}
                      {detail.sales.length > 0 && (
                        <>
                          <tr className="bg-green-50 font-semibold">
                            <td colSpan={6} className="px-4 py-2.5 text-right text-green-700">
                              <span className="inline-flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Earned (Installed)</span>
                            </td>
                            <td className="px-4 py-2.5 text-green-600">{fmt(detail.earnedCommission)}</td>
                          </tr>
                          <tr className="bg-blue-50 font-semibold">
                            <td colSpan={6} className="px-4 py-2.5 text-right text-blue-700">
                              <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Pending (Pipeline)</span>
                            </td>
                            <td className="px-4 py-2.5 text-blue-500">{fmt(detail.pendingCommission)}</td>
                          </tr>
                          <tr className="bg-gray-100 font-bold">
                            <td colSpan={6} className="px-4 py-2.5 text-right text-gray-700">Total (Eventual)</td>
                            <td className="px-4 py-2.5 text-gray-800">{fmt(detail.totalCommission)}</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Manager overrides — earnings from the team */}
              {detail.managerSales && detail.managerSales.length > 0 && (
                <div className="bg-white rounded-xl border overflow-hidden">
                  <div className="px-4 py-3 border-b bg-indigo-50 flex items-center justify-between">
                    <h2 className="font-semibold text-indigo-800 flex items-center gap-2">
                      <Users className="w-4 h-4" /> Team Override Earnings
                    </h2>
                    <span className="text-xs text-indigo-600">
                      {detail.managerSales.length} {detail.managerSales.length === 1 ? 'sale' : 'sales'} from your team
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-500">Salesman</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-500">Customer</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-500">Deal Total</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-500">Rate</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-500">Your Cut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {detail.managerSales.map(s => {
                          const st = STATUS_STYLE[s.status] || { label: s.status, cls: 'bg-gray-100 text-gray-500' };
                          const isEarned = s.status === 'completed';
                          return (
                            <tr key={`mgr-${s.id}`} className={`hover:bg-gray-50 ${isEarned ? '' : 'bg-blue-50/30'}`}>
                              <td className="px-4 py-3 text-gray-500">{s.created_at?.split('T')[0]}</td>
                              <td className="px-4 py-3 text-gray-700">{s.salesman_name}</td>
                              <td className="px-4 py-3 font-medium">{s.customer_name}</td>
                              <td className="px-4 py-3">{fmt(s.deal_total)}</td>
                              <td className="px-4 py-3">{((s.manager_rate || 0) * 100).toFixed(0)}%</td>
                              <td className="px-4 py-3">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                              </td>
                              <td className={`px-4 py-3 font-semibold ${isEarned ? 'text-indigo-600' : 'text-blue-500'}`}>
                                {fmt(s.manager_amount)}
                                {!isEarned && <span className="ml-1 text-[10px] font-normal text-blue-400">pending</span>}
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="bg-indigo-50 font-semibold">
                          <td colSpan={6} className="px-4 py-2.5 text-right text-indigo-700">
                            <span className="inline-flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Earned (Installed)</span>
                          </td>
                          <td className="px-4 py-2.5 text-indigo-600">{fmt(detail.earnedManager)}</td>
                        </tr>
                        <tr className="bg-blue-50 font-semibold">
                          <td colSpan={6} className="px-4 py-2.5 text-right text-blue-700">
                            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Pending (Pipeline)</span>
                          </td>
                          <td className="px-4 py-2.5 text-blue-500">{fmt(detail.pendingManager)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Payout history */}
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <h2 className="font-semibold text-gray-700">Payout History</h2>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Amount</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Notes</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Paid By</th>
                      {isAdmin && <th className="px-4 py-3"></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {detail.payouts.map(p => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">{p.paid_date}</td>
                        <td className="px-4 py-3 font-semibold text-blue-600">{fmt(p.amount)}</td>
                        <td className="px-4 py-3 text-gray-400">{p.notes || '-'}</td>
                        <td className="px-4 py-3 text-gray-500">{p.paid_by_name || '-'}</td>
                        {isAdmin && (
                          <td className="px-4 py-3">
                            <button onClick={() => handleDeletePayout(p.id)} className="text-red-400 hover:text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {detail.payouts.length === 0 && (
                      <tr><td colSpan={isAdmin ? 5 : 4} className="px-4 py-6 text-center text-gray-400">No payouts recorded</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Log payout form (admin only) */}
              {isAdmin && (
                <div className="bg-white rounded-xl border p-5">
                  <h2 className="font-semibold text-gray-700 mb-4">Record Payout</h2>
                  <form onSubmit={handleLogPayout}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          required
                          value={payoutForm.amount}
                          onChange={e => setPayoutForm(f => ({ ...f, amount: e.target.value }))}
                          className={inputCls}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Date</label>
                        <input
                          type="date"
                          required
                          value={payoutForm.paid_date}
                          onChange={e => setPayoutForm(f => ({ ...f, paid_date: e.target.value }))}
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Notes</label>
                        <input
                          type="text"
                          value={payoutForm.notes}
                          onChange={e => setPayoutForm(f => ({ ...f, notes: e.target.value }))}
                          className={inputCls}
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={savingPayout || !payoutForm.amount}
                      className="px-5 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50">
                      {savingPayout ? 'Saving...' : 'Record Payout'}
                    </button>
                  </form>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!selectedId && isAdmin && (
        <p className="text-gray-400 text-sm mt-4">Select a salesman above to view their commission details.</p>
      )}
    </div>
  );
}
